import {
    Add,
    BlockOutlined,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import {
    Button,
    Chip,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ConfirmationModal from '../../../components/ConfirmationModal';
import ListPageLayout from '../../../components/ListPageLayout';
import useErrors from '../../../hooks/useErrors';
import { useFetch } from '../../../hooks/useFecth';
import server from '../../../services/server';
import AsaasAccountCard from './components/AsaasAccountCard';
import DialogAsaasWebhook from './components/DialogAsaasWebhook';

function summarizeEvents(events = []) {
    if (!Array.isArray(events) || !events.length) return '—';
    const payments = events.filter(e => String(e).startsWith('PAYMENT_')).length;
    const subscriptions = events.filter(e => String(e).startsWith('SUBSCRIPTION_')).length;
    const checkout = events.filter(e => String(e).startsWith('CHECKOUT_')).length;
    const parts = [];
    if (payments) parts.push(`${payments} cobrança${payments > 1 ? 's' : ''}`);
    if (subscriptions) parts.push(`${subscriptions} assinatura${subscriptions > 1 ? 's' : ''}`);
    if (checkout) parts.push(`${checkout} checkout`);
    return parts.length ? parts.join(' · ') : `${events.length} eventos`;
}

export default function AsaasWebhooks() {
    const { data, mutate, isFetching } = useFetch('settings/asaas-webhooks');
    const { data: account, isFetching: loadingAccount, error: accountError } = useFetch('settings/asaas-webhooks/account');
    const { setErrors, enqueueSnackbar } = useErrors();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const webhooks = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const handleDelete = webhookId => {
        setDeletingId(webhookId);
        setConfirmAction(() => async () => {
            try {
                await server.delete(`settings/asaas-webhooks/${webhookId}`);
                enqueueSnackbar('Webhook removido.');
                await mutate();
            } catch (error) {
                setErrors(error);
            }
            setConfirmOpen(false);
            setDeletingId(null);
        });
        setConfirmOpen(true);
    };

    return (
        <>
            <ListPageLayout
                title="Webhooks Asaas"
                subtitle="Mesmo Asaas de propostas, planos e contratos (src/service/Asaas.js + ASAAS_KEY). Não é a key do clube."
                intro={
                    <AsaasAccountCard
                        data={account}
                        loading={loadingAccount}
                        error={accountError}
                        webhooks={webhooks}
                    />
                }
                counts={[{ label: 'webhooks', value: webhooks.length, color: 'primary' }]}
                loading={isFetching && !data}
                showViewToggle={false}
                actions={
                    <Button size="small" variant="contained" startIcon={<Add />} component={Link} to="#webhook">
                        Novo webhook
                    </Button>
                }
                isEmpty={!isFetching && !webhooks.length}
                empty={
                    <Stack direction="row" justifyContent="center" alignItems="center" gap={1}>
                        <Typography color="text.secondary">Nenhum webhook encontrado.</Typography>
                        <BlockOutlined />
                    </Stack>
                }
            >
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell>Eventos</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" width={120} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {webhooks.map(webhook => {
                                const id = webhook.id || webhook.webhookId;
                                const enabled = webhook.enabled !== false && webhook.active !== false;
                                const interrupted = Boolean(webhook.interrupted);
                                return (
                                    <TableRow key={id || webhook.url} hover>
                                        <TableCell>{webhook.name || '—'}</TableCell>
                                        <TableCell sx={{ maxWidth: 320 }}>
                                            <Typography variant="body2" noWrap title={webhook.url}>
                                                {webhook.url || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{summarizeEvents(webhook.events)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" gap={0.5} flexWrap="wrap">
                                                <Chip
                                                    size="small"
                                                    label={enabled ? 'Ativo' : 'Inativo'}
                                                    color={enabled ? 'success' : 'default'}
                                                    variant="outlined"
                                                />
                                                {interrupted && (
                                                    <Chip
                                                        size="small"
                                                        label="Fila interrompida"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton component={Link} to={`#webhook|${id}`} title="Editar" size="small">
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDelete(id)}
                                                title="Remover"
                                                size="small"
                                                color="error"
                                                disabled={deletingId === id}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ListPageLayout>

            <DialogAsaasWebhook mutate={mutate} />

            <ConfirmationModal
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setDeletingId(null);
                }}
                onConfirm={confirmAction}
                title="Remover webhook"
                message="Tem certeza que deseja remover este webhook da conta Eurobem no Asaas?"
            />
        </>
    );
}
